import { useEffect, useState } from "react";
import axios from "./api/axios";
import useAuth from "./hooks/useAuth";
import i18n from "./i18n";
import { defaultLanguage } from "./constants";

const WEBSITE_URL_ACC = "/dashboard";
const WEBSITE_URL_CONTACT = "/contact/key";

const StructuredData = () => {
  const { username } = useAuth();
  const [schema, setSchema] = useState(null);

  useEffect(() => {
    if (!username) return;

    const fetchData = async () => {
      try {
        const art = defaultLanguage;
        const title = "accomodationForm";

        const accRes = await axios.get(WEBSITE_URL_ACC, {
          params: { username, art, title },
        });

        if (!accRes.data.entry) return;
        const entry = accRes.data.entry;

        const getContactField = async (key) => {
          const res = await axios.get(WEBSITE_URL_CONTACT, {
            params: { username, key },
          });
          return res.data[key];
        };

        const city = await getContactField("city");
        const country = await getContactField("country");
        const street = await getContactField("street");
        const number = await getContactField("number");
        const zip = await getContactField("zip");
        const tourismLicense = await getContactField("tourismLicense");
        const lat = await getContactField("positionLat");
        const lng = await getContactField("positionLng");
        const phone = await getContactField("tel");

        const accTypeCode = entry.accomodationType;
        const accTypeLabel = i18n.t(`codes.acc_types.${accTypeCode}`);

        const hostname = window.location.hostname
          .replace(/^www\./, "")
          .split(".")[0];

        let googleRating = null;
        let reviewCount = null;
        let reviewArray = [];

        try {
          const googleRes = await axios.get(`google-reviews/${hostname}`);
          const data = googleRes.data;

          googleRating = data.rating;
          reviewCount = data.reviews?.length || 0;


          reviewArray =
            data.reviews?.map((r) => ({
                "@type": "Review",
                author: {
                "@type": "Person",
                name: r.author_name,
                },
                reviewRating: {
                "@type": "Rating",
                ratingValue: Number(r.rating),
                bestRating: "5",
                worstRating: "1",
                },
                reviewBody: r.text,
                datePublished: new Date(r.time * 1000)
                .toISOString()
                .split("T")[0],
            })) || [];
        } catch (err) {
          console.error("Google reviews failed for schema");
        }

        let faqSchema = null;

        try {
            const faqRes = await axios.get("/websitejson", {
                params: { username, art:"en", title: "reviews-FAQ" },
            });

            if (faqRes.data.entry && faqRes.data.entry.content) {
                const faqContent = JSON.parse(faqRes.data.entry.content);

                if (Array.isArray(faqContent) && faqContent.length > 0) {
                faqSchema = {
                    "@context": "https://schema.org",
                    "@type": "FAQPage",
                    mainEntity: faqContent.map((item) => ({
                    "@type": "Question",
                    name: item.intro,
                    acceptedAnswer: {
                        "@type": "Answer",
                        text: item.text,
                    },
                    })),
                };
                }
            }
        } catch (err) {
            console.error("FAQ schema error:", err);
        }

        const vacationRentalSchema = {
            "@type": "VacationRental",

            name: entry.nameAccommodation || username,
            description: entry.description || undefined,

            image: [
                window.location.origin + "/Logo_light.svg",
            ],

            additionalType: accTypeLabel || undefined,
            url: window.location.origin,

            address: {
                "@type": "PostalAddress",
                streetAddress: `${street} ${number}`,
                addressLocality: city,
                postalCode: zip,
                addressCountry: country,
            },

            geo: lat && lng ? {
                "@type": "GeoCoordinates",
                latitude: lat,
                longitude: lng,
            } : undefined,

            containsPlace: {
                "@type": "Accommodation",
                numberOfBedrooms: entry.bedrooms,
                numberOfBathroomsTotal: entry.bathrooms,
                occupancy: {
                    "@type": "QuantitativeValue",
                    value: entry.guests
                }
            },

            minimumNights: entry.numNights,

            checkinTime: entry.checkIn,
            checkoutTime: entry.checkOut,

             offers: {
                "@type": "Offer",
                priceCurrency: entry.currency,
                price: entry.lowerPrice,
                availability: "https://schema.org/InStock",

                eligibleDuration: entry.numNights
                ? {
                    "@type": "QuantitativeValue",
                    value: entry.numNights,
                    unitCode: "DAY",
                    }
                : undefined,
            },

            aggregateRating:
               {
                "@type": "AggregateRating",
                ratingValue: Number(googleRating),
                reviewCount: Number(reviewCount),
                },

                review:
                reviewArray.length > 0
                    ? reviewArray
                    : undefined,

                identifier: tourismLicense || undefined,
                };

         const organizationSchema = {
            "@type": "LodgingBusiness",
            name: entry.nameAccommodation || username,
            url: window.location.origin,
            logo: window.location.origin + "/Logo_light.svg",
            image: window.location.origin + "/Logo_light.svg",
            telephone: phone || undefined,
            priceRange:
                entry.lowerPrice && entry.currency
                ? `${entry.currency} ${entry.lowerPrice}+`
                : undefined,
        };

        const graph = {
          "@context": "https://schema.org",
          "@graph": [
            vacationRentalSchema,
            organizationSchema,
            ...(faqSchema ? [faqSchema] : []),
          ],
        };

        setSchema(JSON.parse(JSON.stringify(graph)));
      } catch (error) {
        console.error("Structured data error:", error);
      }
    };

    fetchData();
  }, [username]);

  useEffect(() => {
    if (!schema) return;

    const existing = document.getElementById("structured-data");
    if (existing) existing.remove();

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "structured-data";
    script.innerHTML = JSON.stringify(schema);

    document.head.appendChild(script);
  }, [schema]);

  return null;
};

export default StructuredData;
